import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Package, Clock, CheckCircle, XCircle, User, MapPin, Lock, Eye, EyeOff, Truck, CreditCard, Mail, Phone, Calendar } from 'lucide-react';
import axios from 'axios';
import Navbar from '@/components/Navbar';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const UserDashboard = () => {
  const { user, token, logout, updateUser } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('orders');
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancellingOrder, setCancellingOrder] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  
  // Profile state
  const [profile, setProfile] = useState({
    full_name: user?.full_name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    date_of_birth: user?.date_of_birth || '',
    gender: user?.gender || ''
  });
  const [savingProfile, setSavingProfile] = useState(false);
  
  // Password state
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [changingPassword, setChangingPassword] = useState(false);
  
  // Addresses state
  const [addresses, setAddresses] = useState([]);
  const [editingAddress, setEditingAddress] = useState(null);
  const [newAddress, setNewAddress] = useState({
    label: '',
    full_name: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    postal_code: '',
    country: '',
    phone: '',
    is_default: false
  });

  useEffect(() => {
    fetchOrders();
    fetchAddresses();
    if (user) {
      setProfile({
        full_name: user.full_name || '',
        email: user.email || '',
        phone: user.phone || '',
        date_of_birth: user.date_of_birth || '',
        gender: user.gender || ''
      });
    }
  }, [user]);

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders(response.data);
    } catch (error) {
      console.error('Failed to fetch orders', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAddresses = async () => {
    try {
      const response = await axios.get(`${API}/user/addresses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAddresses(response.data || []);
    } catch (error) {
      console.error('Failed to fetch addresses', error);
    }
  };

  const handleCancelOrder = async (orderId) => {
    if (!window.confirm('Are you sure you want to cancel this order?')) return;
    
    setCancellingOrder(orderId);
    try {
      await axios.post(`${API}/orders/${orderId}/cancel`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Order cancelled successfully');
      fetchOrders();
      setSelectedOrder(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel order');
    } finally {
      setCancellingOrder(null);
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      const response = await axios.put(`${API}/user/profile`, profile, {
        headers: { Authorization: `Bearer ${token}` }
      });
      updateUser(response.data);
      toast.success('Profile updated successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (passwordData.new_password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setChangingPassword(true);
    try {
      await axios.post(`${API}/user/change-password`, {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Password changed successfully');
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleSaveAddress = async (e) => {
    e.preventDefault();
    try {
      if (editingAddress) {
        await axios.put(`${API}/user/addresses/${editingAddress.id}`, newAddress, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Address updated');
      } else {
        await axios.post(`${API}/user/addresses`, newAddress, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Address added');
      }
      fetchAddresses();
      setNewAddress({
        label: '', full_name: '', address_line1: '', address_line2: '',
        city: '', state: '', postal_code: '', country: '', phone: '', is_default: false
      });
      setEditingAddress(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save address');
    }
  };

  const handleDeleteAddress = async (addressId) => {
    if (!window.confirm('Delete this address?')) return;
    try {
      await axios.delete(`${API}/user/addresses/${addressId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Address deleted');
      fetchAddresses();
    } catch (error) {
      toast.error('Failed to delete address');
    }
  };

  const canCancelOrder = (order) => {
    const status = order.maksura_tracking?.status || 'pending';
    return !['in_transit', 'delivered', 'cancelled'].includes(status) && order.status !== 'cancelled';
  };

  const getStatusIcon = (status) => {
    if (status === 'delivered') return <CheckCircle className="h-5 w-5 text-green-600" />;
    if (status === 'cancelled') return <XCircle className="h-5 w-5 text-red-600" />;
    if (status === 'processing' || status === 'in_transit') return <Clock className="h-5 w-5 text-blue-600" />;
    return <Package className="h-5 w-5 text-gray-400" />;
  };

  const tabs = [
    { id: 'orders', label: 'My Orders', icon: Package },
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'addresses', label: 'Addresses', icon: MapPin },
    { id: 'security', label: 'Security', icon: Lock }
  ];

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900">
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-bold mb-2" data-testid="dashboard-title">My Account</h1>
          <p className="text-gray-600">Welcome back, {user?.full_name}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm p-4">
              <nav className="space-y-1">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${
                      activeTab === tab.id
                        ? 'bg-black text-white'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <tab.icon className="h-5 w-5" />
                    {tab.label}
                  </button>
                ))}
              </nav>
              
              <hr className="my-4" />
              
              <button
                onClick={() => { logout(); navigate('/'); }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-red-600 hover:bg-red-50 transition-colors"
              >
                <XCircle className="h-5 w-5" />
                Logout
              </button>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            {/* Orders Tab */}
            {activeTab === 'orders' && (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h2 className="text-xl font-bold mb-6">My Orders</h2>
                
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black"></div>
                  </div>
                ) : orders.length === 0 ? (
                  <div className="text-center py-12" data-testid="no-orders">
                    <Package className="h-16 w-16 mx-auto text-gray-300 mb-4" />
                    <p className="text-gray-600 mb-4">You haven't placed any orders yet</p>
                    <Link to="/products">
                      <Button className="bg-black text-white hover:bg-zinc-800">
                        Start Shopping
                      </Button>
                    </Link>
                  </div>
                ) : (
                  <div className="space-y-4" data-testid="orders-list">
                    {orders.map((order) => (
                      <div
                        key={order.id}
                        className="border rounded-lg p-4 hover:border-gray-400 transition-colors cursor-pointer"
                        onClick={() => setSelectedOrder(order)}
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            {getStatusIcon(order.maksura_tracking?.status || order.status)}
                            <div>
                              <p className="font-medium">Order #{order.id.substring(0, 8)}</p>
                              <p className="text-sm text-gray-500">
                                {new Date(order.created_at).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-bold">${order.total_amount?.toFixed(2)}</p>
                            <p className={`text-sm capitalize ${
                              order.payment_status === 'paid' ? 'text-green-600' :
                              order.payment_status === 'cancelled' ? 'text-red-600' :
                              'text-yellow-600'
                            }`}>
                              {order.payment_status}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <span>{order.items?.length || 0} item(s)</span>
                          <span>•</span>
                          <span className="capitalize">{order.maksura_tracking?.status || 'pending'}</span>
                        </div>
                        
                        {order.tracking_token && (
                          <Link
                            to={`/tracking/${order.id}?token=${order.tracking_token}`}
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex items-center gap-1 mt-3 text-sm text-blue-600 hover:underline"
                          >
                            <Truck className="h-4 w-4" />
                            Track Order
                          </Link>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Profile Tab */}
            {activeTab === 'profile' && (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h2 className="text-xl font-bold mb-6">Profile Information</h2>
                
                <form onSubmit={handleUpdateProfile} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label>Full Name</Label>
                      <Input
                        value={profile.full_name}
                        onChange={(e) => setProfile({ ...profile, full_name: e.target.value })}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>Email</Label>
                      <Input
                        type="email"
                        value={profile.email}
                        disabled
                        className="mt-1 bg-gray-100"
                      />
                      <p className="text-xs text-gray-500 mt-1">Email cannot be changed</p>
                    </div>
                    <div>
                      <Label>Phone</Label>
                      <Input
                        value={profile.phone}
                        onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                        placeholder="+1234567890"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>Date of Birth</Label>
                      <Input
                        type="date"
                        value={profile.date_of_birth}
                        onChange={(e) => setProfile({ ...profile, date_of_birth: e.target.value })}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>Gender</Label>
                      <select
                        value={profile.gender}
                        onChange={(e) => setProfile({ ...profile, gender: e.target.value })}
                        className="mt-1 w-full border rounded-md px-3 py-2"
                      >
                        <option value="">Prefer not to say</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                  </div>
                  
                  <Button type="submit" disabled={savingProfile} className="bg-black text-white hover:bg-zinc-800">
                    {savingProfile ? 'Saving...' : 'Save Changes'}
                  </Button>
                </form>
              </div>
            )}

            {/* Addresses Tab */}
            {activeTab === 'addresses' && (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h2 className="text-xl font-bold mb-6">Saved Addresses</h2>
                
                {/* Address Form */}
                <form onSubmit={handleSaveAddress} className="mb-8 p-4 border rounded-lg bg-gray-50">
                  <h3 className="font-medium mb-4">{editingAddress ? 'Edit Address' : 'Add New Address'}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Label (e.g., Home, Office)</Label>
                      <Input
                        value={newAddress.label}
                        onChange={(e) => setNewAddress({ ...newAddress, label: e.target.value })}
                        className="mt-1"
                        required
                      />
                    </div>
                    <div>
                      <Label>Full Name</Label>
                      <Input
                        value={newAddress.full_name}
                        onChange={(e) => setNewAddress({ ...newAddress, full_name: e.target.value })}
                        className="mt-1"
                        required
                      />
                    </div>
                    <div className="md:col-span-2">
                      <Label>Address Line 1</Label>
                      <Input
                        value={newAddress.address_line1}
                        onChange={(e) => setNewAddress({ ...newAddress, address_line1: e.target.value })}
                        className="mt-1"
                        required
                      />
                    </div>
                    <div className="md:col-span-2">
                      <Label>Address Line 2 (Optional)</Label>
                      <Input
                        value={newAddress.address_line2}
                        onChange={(e) => setNewAddress({ ...newAddress, address_line2: e.target.value })}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>City</Label>
                      <Input
                        value={newAddress.city}
                        onChange={(e) => setNewAddress({ ...newAddress, city: e.target.value })}
                        className="mt-1"
                        required
                      />
                    </div>
                    <div>
                      <Label>State/Province</Label>
                      <Input
                        value={newAddress.state}
                        onChange={(e) => setNewAddress({ ...newAddress, state: e.target.value })}
                        className="mt-1"
                        required
                      />
                    </div>
                    <div>
                      <Label>Postal Code</Label>
                      <Input
                        value={newAddress.postal_code}
                        onChange={(e) => setNewAddress({ ...newAddress, postal_code: e.target.value })}
                        className="mt-1"
                        required
                      />
                    </div>
                    <div>
                      <Label>Country</Label>
                      <Input
                        value={newAddress.country}
                        onChange={(e) => setNewAddress({ ...newAddress, country: e.target.value })}
                        className="mt-1"
                        required
                      />
                    </div>
                    <div>
                      <Label>Phone</Label>
                      <Input
                        value={newAddress.phone}
                        onChange={(e) => setNewAddress({ ...newAddress, phone: e.target.value })}
                        className="mt-1"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={newAddress.is_default}
                        onChange={(e) => setNewAddress({ ...newAddress, is_default: e.target.checked })}
                        className="h-4 w-4"
                      />
                      <Label>Set as default address</Label>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-4">
                    <Button type="submit" className="bg-black text-white hover:bg-zinc-800">
                      {editingAddress ? 'Update Address' : 'Add Address'}
                    </Button>
                    {editingAddress && (
                      <Button type="button" variant="outline" onClick={() => {
                        setEditingAddress(null);
                        setNewAddress({
                          label: '', full_name: '', address_line1: '', address_line2: '',
                          city: '', state: '', postal_code: '', country: '', phone: '', is_default: false
                        });
                      }}>
                        Cancel
                      </Button>
                    )}
                  </div>
                </form>
                
                {/* Saved Addresses */}
                {addresses.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No saved addresses</p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {addresses.map((address) => (
                      <div key={address.id} className={`border rounded-lg p-4 ${address.is_default ? 'border-black' : ''}`}>
                        {address.is_default && (
                          <span className="text-xs bg-black text-white px-2 py-1 rounded mb-2 inline-block">Default</span>
                        )}
                        <p className="font-medium">{address.label}</p>
                        <p className="text-sm text-gray-600">{address.full_name}</p>
                        <p className="text-sm text-gray-600">{address.address_line1}</p>
                        {address.address_line2 && <p className="text-sm text-gray-600">{address.address_line2}</p>}
                        <p className="text-sm text-gray-600">
                          {address.city}, {address.state} {address.postal_code}
                        </p>
                        <p className="text-sm text-gray-600">{address.country}</p>
                        {address.phone && <p className="text-sm text-gray-600">{address.phone}</p>}
                        
                        <div className="flex gap-2 mt-3">
                          <button
                            onClick={() => {
                              setEditingAddress(address);
                              setNewAddress(address);
                            }}
                            className="text-sm text-blue-600 hover:underline"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteAddress(address.id)}
                            className="text-sm text-red-600 hover:underline"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Security Tab */}
            {activeTab === 'security' && (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h2 className="text-xl font-bold mb-6">Change Password</h2>
                
                <form onSubmit={handleChangePassword} className="max-w-md space-y-4">
                  <div>
                    <Label>Current Password</Label>
                    <div className="relative mt-1">
                      <Input
                        type={showPasswords.current ? 'text' : 'password'}
                        value={passwordData.current_password}
                        onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPasswords({ ...showPasswords, current: !showPasswords.current })}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
                      >
                        {showPasswords.current ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  
                  <div>
                    <Label>New Password</Label>
                    <div className="relative mt-1">
                      <Input
                        type={showPasswords.new ? 'text' : 'password'}
                        value={passwordData.new_password}
                        onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                        required
                        minLength={6}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
                      >
                        {showPasswords.new ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  
                  <div>
                    <Label>Confirm New Password</Label>
                    <div className="relative mt-1">
                      <Input
                        type={showPasswords.confirm ? 'text' : 'password'}
                        value={passwordData.confirm_password}
                        onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
                      >
                        {showPasswords.confirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  
                  <Button type="submit" disabled={changingPassword} className="bg-black text-white hover:bg-zinc-800">
                    {changingPassword ? 'Changing...' : 'Change Password'}
                  </Button>
                </form>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Order Detail Modal */}
      {selectedOrder && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold">Order Details</h2>
                <button onClick={() => setSelectedOrder(null)} className="text-gray-500 hover:text-black">
                  <XCircle className="h-6 w-6" />
                </button>
              </div>
              
              <div className="space-y-6">
                <div>
                  <p className="text-sm text-gray-500">Order ID</p>
                  <p className="font-mono">{selectedOrder.id}</p>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Order Date</p>
                    <p>{new Date(selectedOrder.created_at).toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Payment Status</p>
                    <p className={`font-medium capitalize ${
                      selectedOrder.payment_status === 'paid' ? 'text-green-600' :
                      selectedOrder.payment_status === 'cancelled' ? 'text-red-600' :
                      'text-yellow-600'
                    }`}>{selectedOrder.payment_status}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Payment Method</p>
                    <p className="capitalize">{selectedOrder.payment_method?.replace('_', ' ')}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Tracking Status</p>
                    <p className="capitalize">{selectedOrder.maksura_tracking?.status || 'Pending'}</p>
                  </div>
                </div>
                
                <div>
                  <p className="text-sm text-gray-500 mb-2">Items</p>
                  <div className="space-y-2">
                    {selectedOrder.items?.map((item, idx) => (
                      <div key={idx} className="flex items-center gap-3 p-3 bg-gray-50 rounded">
                        {item.image && (
                          <img src={item.image} alt={item.name} className="w-12 h-12 object-cover rounded" />
                        )}
                        <div className="flex-1">
                          <p className="font-medium">{item.name || `Product ${item.product_id?.substring(0, 8)}`}</p>
                          <p className="text-sm text-gray-500">Qty: {item.quantity} × ${item.price?.toFixed(2)}</p>
                        </div>
                        <p className="font-medium">${(item.quantity * item.price)?.toFixed(2)}</p>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div>
                  <p className="text-sm text-gray-500 mb-2">Shipping Address</p>
                  <div className="p-3 bg-gray-50 rounded">
                    <p>{selectedOrder.shipping_address?.full_name}</p>
                    <p className="text-sm text-gray-600">
                      {selectedOrder.shipping_address?.address_line1 || selectedOrder.shipping_address?.street}<br />
                      {selectedOrder.shipping_address?.city}, {selectedOrder.shipping_address?.state} {selectedOrder.shipping_address?.postal_code || selectedOrder.shipping_address?.zip_code}<br />
                      {selectedOrder.shipping_address?.country}
                    </p>
                  </div>
                </div>
                
                <div className="border-t pt-4">
                  <div className="flex justify-between text-lg font-bold">
                    <span>Total</span>
                    <span>${selectedOrder.total_amount?.toFixed(2)}</span>
                  </div>
                </div>
                
                <div className="flex gap-3">
                  {selectedOrder.tracking_token && (
                    <Link
                      to={`/tracking/${selectedOrder.id}?token=${selectedOrder.tracking_token}`}
                      className="flex-1"
                    >
                      <Button className="w-full bg-blue-600 text-white hover:bg-blue-700">
                        <Truck className="h-4 w-4 mr-2" />
                        Track Order
                      </Button>
                    </Link>
                  )}
                  {canCancelOrder(selectedOrder) && (
                    <Button
                      onClick={() => handleCancelOrder(selectedOrder.id)}
                      disabled={cancellingOrder === selectedOrder.id}
                      variant="outline"
                      className="border-red-500 text-red-500 hover:bg-red-50"
                    >
                      {cancellingOrder === selectedOrder.id ? 'Cancelling...' : 'Cancel Order'}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserDashboard;
